import { useSelector } from 'react-redux';
import tractSocioeconomicSource from 'data/sources/tractSocioeconomicSource';
import { socioeconomicFactorRadius } from 'utils/socioeconomicScores';
import { IconLayer } from '@deck.gl/layers';
import { CollisionFilterExtension } from '@deck.gl/extensions';
import MapPointer from 'assets/img/map-pointer.png';
import { useEffect, useState } from 'react';
// import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import { useSearchParams } from 'react-router-dom';
// import { CompositeLayer } from '@deck.gl/core';
import _MapPointerBlueSquare from 'assets/img/map-pointer-blue-square.png';
// import MapPointersSprite from 'assets/img/map-pointers-sprite.png';
import _MapPointerGreenSquare from 'assets/img/map-pointer-green-square.png';
import _MapPointerPinkSquare from 'assets/img/map-pointer-pink-square.png';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import ReactDOMServer from 'react-dom/server';
import Popover from '../common/Popover';
import { calculateBounds, isWithinBounds } from 'utils/bounds';

export const TRACT_SOCIOECONOMIC_LAYER_ID = 'tractSocioeconomicLayer';
export const ACTIVE_TRACT_ZOOMED_OUT_POINTER_LAYER_ID =
  'activeTractZoomedOutPointerLayer';

export default function TractSocioeconomicLayer() {
  const [layer, setLayer] = useState(null);
  const [previousActiveTract, setPreviousActiveTract] = useState(null);
  const [previousActiveCompareTract, setPreviousActiveCompareTract] = useState(null);
  const [previousActiveCompareSimilarTract, setPreviousActiveCompareSimilarTract] =
    useState(null);
  const [previousSimilarTracts, setPreviousSimilarTracts] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const [previousLongitude, setPreviousLongitude] = useState(null);
  const [previousLatitude, setPreviousLatitude] = useState(null);
  const [previousZoom, setPreviousZoom] = useState(null);
  const { tractSocioeconomicLayer } = useSelector((state) => state.carto.layers);
  const source = tractSocioeconomicSource(
    tractSocioeconomicLayer?.year || 2019,
    tractSocioeconomicLayer?.socioeconomicFactor || 'overall_socioeconomic'
  );
  const viewState = useSelector((state) => state.carto.viewState);
  const [searchParams, _setSearchParams] = useSearchParams();

  useEffect(() => {
    const { longitude, latitude, zoom } = viewState;
    const bounds = calculateBounds(longitude, latitude, zoom);

    const visibleData = tractSocioeconomicLayer?.data.filter((item) =>
      isWithinBounds(item, bounds)
    );

    if (!tractSocioeconomicLayer || !source) {
      return;
    }

    const isSocioeconomicFactorsLayerActive =
      searchParams.get('socioeconomic-layer') !== null
        ? searchParams.get('socioeconomic-layer') === 'true'
        : true;

    if (isSocioeconomicFactorsLayerActive === false) {
      setLayer(null);
      return;
    }

    if (isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActiveTract === searchParams.get('active-tract') &&
      previousActiveCompareTract === searchParams.get('active-compare-tract') &&
      previousActiveCompareSimilarTract ===
        searchParams.get('active-compare-similar-tract') &&
      previousSimilarTracts === searchParams.get('similar-tracts') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousYear === searchParams.get('year') &&
      previousLongitude === longitude &&
      previousLatitude === latitude &&
      previousZoom === zoom
    ) {
      return;
    }

    if (
      visibleData?.[0]?.[searchParams.get('socioeconomic-factor')] === undefined ||
      visibleData?.[0]?.year?.toString() !== searchParams.get('year')
    ) {
      return;
    }

    const tractSocioeconomicPointersLayer = new IconLayer({
      id: TRACT_SOCIOECONOMIC_LAYER_ID,
      data: visibleData,
      extensions: [new CollisionFilterExtension()],
      collisionTestProps: {
        sizeScale: 4,
      },
      // pickable: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom),
      pickable: true,
      // opacity: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) ? 1 : 0,
      iconAtlas: MapPointer,
      iconMapping: {
        marker: { x: 0, y: 0, width: 96, height: 97 },
      },
      // visible: false,
      getIcon: () => 'marker',
      sizeScale: 1,
      getSize: (object) => {
        let similarTracts = searchParams.get('similar-tracts')?.split(',');
        let tractCode = object.tractcode?.toString();
        let isActiveCompareSimilarTract =
          searchParams.get('active-compare-similar-tract') === tractCode.toString();
        let isPercentage =
          tractSocioeconomicLayer.socioeconomicFactor.includes('percentage');
        let radius =
          isPercentage === true
            ? (object[tractSocioeconomicLayer.socioeconomicFactor] / 100) * 5
            : object[tractSocioeconomicLayer.socioeconomicFactor];

        if (
          searchParams.get('active-tract') === tractCode ||
          searchParams.get('active-compare-tract') === tractCode ||
          isActiveCompareSimilarTract === true ||
          similarTracts?.includes(tractCode) === true
        ) {
          return socioeconomicFactorRadius(radius);
        }

        return searchParams.get('active-tract') === null
          ? socioeconomicFactorRadius(radius)
          : 0;
      },
      onHover: (info) => {
        if (info?.object) {
          let isPercentage =
            tractSocioeconomicLayer.socioeconomicFactor.includes('percentage');

          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='text-sm'>
                    {tractSocioeconomicLayer.socioeconomicFactorLabel}:{' '}
                    <span className='text-blue-300'>
                      {Math.round(
                        info.object[tractSocioeconomicLayer.socioeconomicFactor] * 100
                      ) / 100}
                      {isPercentage === true ? '%' : ''}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      getPosition: (d) => [d.lng, d.lat],
      updateTriggers: {
        // opacity: [viewState],
        getSize: [
          searchParams.get('active-tract'),
          searchParams.get('active-compare-tract'),
          searchParams.get('active-compare-similar-tract'),
          searchParams.get('similar-tracts'),
        ],
      },
    });

    // let _activeTractZoomedOutPointerLayer = new IconLayer({
    //   data: tractSocioeconomicLayer?.data,
    //   // extensions: [new CollisionFilterExtension()],
    //   // collisionTestProps: {
    //   //   sizeScale: 4,
    //   // },
    //   id: ACTIVE_TRACT_ZOOMED_OUT_POINTER_LAYER_ID,
    //   pointRadiusMinPixels: 2,
    //   getFillColor: [0, 0, 0, 0],
    //   getSize: (object) => {
    //     let similarTracts = searchParams.get('similar-tracts')?.split(',');
    //     let tractCode = object.tractcode?.toString();
    //     let isActiveCompareSimilarTract =
    //       searchParams.get('active-compare-similar-tract') === tractCode.toString();

    //     if (
    //       searchParams.get('active-tract') === tractCode ||
    //       searchParams.get('active-compare-tract') === tractCode ||
    //       isActiveCompareSimilarTract === true ||
    //       similarTracts?.includes(tractCode) === true
    //     ) {
    //       return 50;
    //     }

    //     return 0;
    //   },
    //   iconAtlas: MapPointersSprite,
    //   iconMapping: {
    //     blue: { x: 0, y: 0, width: 84, height: 99 },
    //     green: { x: 84, y: 0, width: 84, height: 99 },
    //     pink: { x: 168, y: 0, width: 84, height: 99 },
    //   },
    //   // opacity: isLayerVisible(LAYER_TYPE.TRACT, viewState.zoom) === false ? 1 : 0,
    //   getIcon: (_object) => {
    //     // if (
    //     //   object.tractcode.toString() === searchParams.get('active-compare-similar-tract')
    //     // ) {
    //     //   return 'pink';
    //     // } else if (
    //     //   searchParams.get('active-panel') === 'compare-similar-tracts' &&
    //     //   object.tractcode.toString() === searchParams.get('active-tract')
    //     // ) {
    //     //   return 'green';
    //     // } else {
    //     //   return 'blue';
    //     // }
    //     return 'blue';
    //   },
    //   sizeScale: 1,
    //   pickable: false,
    //   getPosition: (d) => {
    //     return [d.lng, d.lat];
    //   },
    //   updateTriggers: {
    //     pickable: [viewState],
    //     opacity: [viewState],
    //     getSize: [
    //       searchParams.get('active-tract'),
    //       searchParams.get('active-compare-tract'),
    //       searchParams.get('active-compare-similar-tract'),
    //       searchParams.get('similar-tracts'),
    //     ],
    //     // getIcon: [
    //     //   searchParams.get('active-panel'),
    //     //   searchParams.get('active-compare-similar-tract'),
    //     //   searchParams.get('active-tract'),
    //     // ],
    //   },
    // });

    // class TractSocioeconomicCompositeLayer extends CompositeLayer {
    //   renderLayers() {
    //     return [tractSocioeconomicPointersLayer];
    //     // return [tractSocioeconomicPointersLayer, activeTractZoomedOutPointerLayer];
    //   }
    // }

    // let tractSocioeconomicCompositeLayer = new TractSocioeconomicCompositeLayer({
    //   // onClick: (info) => {
    //   //   setSearchParams((params) => {
    //   //     params.set('active-tract', info.object.properties.tractcode);
    //   //     params.delete('active-county');
    //   //     params.delete('active-state');

    //   //     return params;
    //   //   });

    //   //   zoomTractIntoView({
    //   //     lng: info.object.properties.lng,
    //   //     lat: info.object.properties.lat,
    //   //   });
    //   // },
    //   updateTriggers: {
    //     // lineWidthMinPixels: [hoveredTractTractcode],
    //     opacity: [viewState],
    //     pickable: [viewState],
    //     getSize: [
    //       searchParams.get('active-tract'),
    //       searchParams.get('active-compare-tract'),
    //       searchParams.get('similar-tracts'),
    //       searchParams.get('active-compare-similar-tract'),
    //       searchParams.get('active-panel'),
    //     ],
    //     getIcon: [
    //       searchParams.get('active-panel'),
    //       searchParams.get('active-compare-similar-tract'),
    //       searchParams.get('active-tract'),
    //     ],
    //   },
    // });

    setLayer(tractSocioeconomicPointersLayer);
    setPreviousActiveTract(searchParams.get('active-tract'));
    setPreviousActiveCompareTract(searchParams.get('active-compare-tract'));
    setPreviousActiveCompareSimilarTract(
      searchParams.get('active-compare-similar-tract')
    );
    setPreviousSimilarTracts(searchParams.get('similar-tracts'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousYear(searchParams.get('year'));
    setPreviousLongitude(longitude);
    setPreviousLatitude(latitude);
    setPreviousZoom(zoom);
  }, [
    layer,
    previousActiveCompareSimilarTract,
    previousActiveCompareTract,
    previousActiveTract,
    previousLatitude,
    previousLongitude,
    previousSimilarTracts,
    previousSocioeconomicFactor,
    previousYear,
    previousZoom,
    searchParams,
    source,
    tractSocioeconomicLayer,
    viewState,
  ]);

  return layer;
}
